import { useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Button } from '../../components/ui/Button';
import { Card, CardHeader, StatTile } from '../../components/ui/Card';
import { InfoTip } from '../../components/ui/InfoTip';
import { Nil, Table, TableFooter, THead, Th, Td, Tr } from '../../components/ui/Table';
import { getMatchReviewsInTier, type MatchReviewRow, type MatchScoreTier } from '../../api/signalOps';
import { SegmentedControl, type TabItem } from '../../components/ui/Tabs';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/States';
import { Pagination, usePagination } from '../../components/ui/Pagination';
import { useDecideMatch } from '../../hooks/useSignalOps';
import { formatNumber } from '../../utils/numberFormat';
import { cleanText, humanize, repairTenderText } from './format';
import { AttioCompanyLink } from '../Dashboard/csm/attio';
import { leftOutText } from './labels';
import { whyClauses } from './matchWhy';

/** Country part of the score, on the score's own scale: +0.10 same, −0.25 different, 0 unknown. */
function countryNote(row: MatchReviewRow): string | null {
  const f = (row.features ?? {}) as Record<string, unknown>;
  if (!('countryMatch' in f)) return null;
  if (f.countryMatch === true) return 'same country +0.10';
  if (f.countryMatch === false) return 'different country −0.25';
  if (f.countryMatch === null) return 'country unknown +0.00';
  return null;
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}


/** How the score is built (api match-review.ts matchScoreParts), stated once so the Why column reads on the same scale. */
const SCORE_NOTE =
  'Match score, 0 to 1 = name similarity × 0.75, plus the domains: +0.25 for one domain or one brand label (uk.experian.com and experian.com, evbox.com and ev-box.com), 0 when one name sits inside the other (emerson.com and emersongroup.com: a parent, a group or a different firm, so only you can tell) or a domain is missing, −0.15 for unrelated domains. Government umbrellas and hosting platforms (fed.be, gouv.fr, odoo.com, myshopify.com…) count as different organisations, not one domain. Then +0.10 for the same country or −0.25 for a different one, and −0.10 when the names agree only without “The” or “Group” and the domains do not. The total is capped at 1.00; the Why column shows what the cap took off, so the parts add up to the score. Pairs on one registrable domain (idemia.com and us.idemia.com) are linked automatically and never listed here; so are new pairs scoring 0.86 and above. What is listed is what only a person can settle, the most ambiguous first.';

/** The domain part of the score, in the reviewer's words. */
const DOMAIN_NOTES: Record<string, string> = {
  same: 'same domain',
  same_label: 'same brand label',
  contains: 'one domain inside the other',
  different: 'unrelated domains',
  unknown: 'a domain missing',
};

interface ScoreParts {
  name: number;
  domain: number;
  country: number;
  nameNoise: number;
  cap: number;
}

function scoreParts(row: MatchReviewRow): ScoreParts | null {
  const p = (row.features ?? {}).scoreParts as Record<string, unknown> | undefined;
  if (!p) return null;
  const name = num(p.name);
  const domain = num(p.domain);
  const country = num(p.country);
  if (name === null || domain === null || country === null) return null;
  return { name, domain, country, nameNoise: num(p.nameNoise) ?? 0, cap: num(p.cap) ?? 0 };
}

/**
 * The Why column: every part of the score with its points, so they add up to
 * the score as displayed (matchWhy.ts whyClauses).
 */
function whyParts(row: MatchReviewRow, commonCountry: string | null): string[] {
  const f = (row.features ?? {}) as Record<string, unknown>;
  const nameSim = num(f.nameSimilarity);
  const parts = scoreParts(row);
  const relation = typeof f.domainRelation === 'string' ? f.domainRelation : null;
  if (!parts) {
    // An older API build without the parts: say what it stored, without points.
    return [
      nameSim !== null ? `name ${nameSim.toFixed(2)}` : null,
      relation ? DOMAIN_NOTES[relation] ?? null : null,
      countryNote(row),
    ].filter((x): x is string => !!x);
  }
  return whyClauses({
    parts,
    score: row.score,
    nameSimilarity: nameSim,
    domainNote: relation ? DOMAIN_NOTES[relation] ?? relation : null,
    countryNote: countryNote(row),
    commonCountry,
  });
}

const TIER_LABELS: Record<MatchScoreTier, string> = { all: 'All', top: '1.00', mid: '0.71–0.99', low: '0.70 and below' };

/** Why a pending pair is left out of the queue, in the order the footer lists them. */
const JUNK_LABELS: Record<string, string> = {
  gone_from_attio: 'Attio record since deleted or merged',
  mail_host: 'mail hosts',
  webmail: 'webmail',
  no_name: '“Unknown” ↔ “Unknown”',
  own_company: 'Sequesto itself',
};

const JUNK_NOTE =
  'Left out of the Pending count (they need no decision): pairs where either Attio record has been deleted or merged in Attio since the pair was queued (by the Attio sync’s own reconcile rule); mail hosts (bounce relays, ESP sending and notification subdomains such as “send.”, “e.”, “…mail.”, Amazon SES); webmail (iCloud, Proton, Hotmail, Gmail…); “Unknown” ↔ “Unknown”; and Sequesto itself.';


/** "Microsoft (microsoft.com)" → "Microsoft". */
const labelNameOf = (label: string) => label.replace(/\s*\([^()]*\)\s*$/, '').trim();

/** Labels can carry a character the source lost ("IDS GmbH ? Analysis"): put back only the unambiguous cases. */
const tidy = (label: string) => repairTenderText(cleanText(label));

/**
 * The candidate label arrives as "Name (domain)". Bounce domains such as
 * `…bnc.salesforce.com` are long, so the domain goes on a grey truncated
 * sub-line instead of pushing into the next column.
 */
function CandidateLabel({
  label,
  domain: knownDomain,
  fallback,
}: {
  label: string;
  domain?: string | null;
  /** Grey sub-line when there is no domain to show (e.g. "no domain · 2 Attio records with this name"). */
  fallback?: string | null;
}) {
  const m = /^(.*\S)\s+\(([^()\s]+)\)$/.exec(label);
  const name = m ? m[1]! : label;
  const domain = knownDomain ?? (m ? m[2]! : null);
  return (
    <div className="min-w-0">
      <p className="truncate text-text-primary" title={name}>
        {name}
      </p>
      {domain ? (
        <p className="truncate text-body-xs text-text-tertiary" title={domain}>
          {domain}
        </p>
      ) : fallback ? (
        <p className="text-body-xs text-text-tertiary">{fallback}</p>
      ) : null}
    </div>
  );
}

/**
 * The queue for matches the scorer could not settle.
 *
 * Accepting re-points one row in the identity link table — it does not merge
 * anything, so a wrong decision here is one click to undo and can never drop a
 * hand-typed field. Every verdict is kept with the features that produced the
 * score, which is the training set for automating this later.
 */
export function MatchReviewView() {
  const [tier, setTier] = useState<MatchScoreTier>('all');
  // The ['signal-ops', 'matches'] prefix keeps useDecideMatch's invalidation working.
  const { data, isLoading, isError, error, refetch, isPlaceholderData } = useQuery({
    queryKey: ['signal-ops', 'matches', tier],
    queryFn: () => getMatchReviewsInTier(tier),
    placeholderData: keepPreviousData,
  });
  const decide = useDecideMatch();
  const rows = useMemo(() => data?.rows ?? [], [data]);
  const pager = usePagination(rows, 15);
  const pending = data?.total ?? rows.length;
  const inTier = data?.tierTotal ?? pending;
  const capped = inTier > rows.length;
  const hiddenJunk = data?.hiddenJunk ?? 0;
  const auto = data?.autoMatch;
  const autoLinked = data?.autoLinkedSameDomain ?? 0;
  const tierCounts = data?.tierCounts;
  // A tier with nothing in it (no 1.00 pair is left once same-domain pairs link automatically) is not offered.
  const tierOptions: TabItem<MatchScoreTier>[] = (['all', 'top', 'mid', 'low'] as const)
    .filter((id) => id === 'all' || id === tier || !tierCounts || tierCounts[id] > 0)
    .map((id) => ({
    id,
    label:
      id === 'all'
        ? `${TIER_LABELS.all} · ${formatNumber(pending)}`
        : tierCounts
          ? `${TIER_LABELS[id]} · ${formatNumber(tierCounts[id])}`
          : TIER_LABELS[id],
  }));

  // A score or source every row shares is said once in the header instead of a column.
  const scores = new Set(rows.map((r) => r.score.toFixed(2)));
  const sources = new Set(rows.map((r) => r.source));
  const sharedScore = rows.length > 1 && scores.size === 1 ? [...scores][0] : null;
  const sharedSource = rows.length > 1 && sources.size === 1 ? [...sources][0] : null;
  // The country part most rows share (199 of 200 "same country" on 12 Oct 2026) goes in the header once.
  const countryCounts = new Map<string, number>();
  for (const r of rows) {
    const note = countryNote(r);
    if (note) countryCounts.set(note, (countryCounts.get(note) ?? 0) + 1);
  }
  const [commonCountry, commonCountryCount] = [...countryCounts.entries()].sort((a, b) => b[1] - a[1])[0] ?? [null, 0];
  const countryInHeader = commonCountry && commonCountryCount >= rows.length / 2 ? commonCountry : null;
  const description = [
    sharedScore ? `All scored ${sharedScore} of 1` : null,
    sharedSource ? `all from ${humanize(sharedSource)}` : null,
    countryInHeader
      ? commonCountryCount === rows.length
        ? `all ${countryInHeader}, shown on a row only where it changes the score`
        : `${countryInHeader} on ${formatNumber(commonCountryCount)} of ${formatNumber(rows.length)}, shown on a row only where it changes the score`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const byReason = data?.hiddenByReason;
  const hiddenText = byReason
    ? Object.keys(JUNK_LABELS)
        .filter((reason) => (byReason[reason as keyof typeof byReason] ?? 0) > 0)
        .map((reason) => `${formatNumber(byReason[reason as keyof typeof byReason]!)} ${JUNK_LABELS[reason]}`)
        .join(', ')
    : null;
  // The same name proposed against the same record more than once ("Microsoft" ×2, two
  // Attio records with no company domain): each row says so, and links its own Attio record.
  const sameName = new Map<string, number>();
  for (const r of rows) {
    const k = `${r.candidateLabel}\u0000${r.proposedCanonicalId ?? ''}`;
    sameName.set(k, (sameName.get(k) ?? 0) + 1);
  }

  // The queue also shrinks on its own (same-domain pairs link automatically on each sync): say so once, quietly.
  const firstTotal = useRef<number | null>(null);
  const [shrunkFrom, setShrunkFrom] = useState<number | null>(null);
  useEffect(() => {
    if (data?.total === undefined) return;
    if (firstTotal.current === null) firstTotal.current = data.total;
    else if (data.total < firstTotal.current) setShrunkFrom(firstTotal.current);
  }, [data?.total]);

  const tile = (v: string | undefined) => (isError ? 'Unavailable' : isLoading ? '…' : v ?? 'Unavailable');

  return (
    <div className="space-y-4">
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      <StatTile
        label="Pending"
        value={tile(data ? formatNumber(pending) : undefined)}
        sub={`Pairs waiting for a call · identity matcher${autoLinked ? ` · ${formatNumber(autoLinked)} on one domain linked automatically` : ''}${hiddenJunk ? ` · ${leftOutText(hiddenJunk)}` : ''}`}
      />
      <StatTile
        label="Decided this week"
        value={tile(data?.decidedLast7Days !== undefined ? formatNumber(data.decidedLast7Days) : undefined)}
        sub="Same company or different · last 7 days"
      />
      <StatTile
        label="Auto-match rate"
        value={tile(auto ? (auto.rate === null ? 'None yet' : `${auto.rate.toFixed(1)}%`) : undefined)}
        sub="Company links made without a human · all time"
        tooltip={
          auto
            ? `${formatNumber(auto.auto)} linked automatically ÷ (${formatNumber(auto.auto)} automatic + ${formatNumber(
                auto.human,
              )} decided by a person + ${formatNumber(auto.pending)} still waiting).`
            : undefined
        }
      />
    </div>
    <Card padding="sm">
      <CardHeader
        title={
          data
            ? capped
              ? `${formatNumber(inTier)} pairs ${tier === 'all' ? 'to review' : `scored ${TIER_LABELS[tier]}`} · top ${formatNumber(rows.length)} most ambiguous first`
              : `${formatNumber(rows.length)} pairs ${tier === 'all' ? 'to review' : `scored ${TIER_LABELS[tier]}`}`
            : 'Pairs to review'
        }
        description={description ? description.charAt(0).toUpperCase() + description.slice(1) : undefined}
        action={
          <span className="inline-flex flex-wrap items-center gap-2">
            {data?.tierCounts && (
              <SegmentedControl options={tierOptions} value={tier} onChange={setTier} ariaLabel="Score tier" />
            )}
            <InfoTip>
            <p className="mb-2">{SCORE_NOTE}</p>
            <p>
              “Same company” points the source row at the proposed record (no merge, one click to undo); “Different”
              leaves both as they are.
            </p>
            </InfoTip>
          </span>
        }
      />

      {decide.isError && <ErrorState title="Couldn’t record that decision" error={decide.error} compact />}
      {shrunkFrom !== null && shrunkFrom > pending && (
        <p className="text-body-xs text-text-tertiary">
          The queue went from {formatNumber(shrunkFrom)} to {formatNumber(pending)} pending since you opened this page: your
          decisions, plus pairs the matcher linked on its own.
        </p>
      )}

      {isLoading ? (
        <LoadingState rows={6} />
      ) : isError ? (
        <ErrorState title="Couldn’t load the review queue" error={error} onRetry={() => refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Nothing waiting"
          description={
            tier === 'all'
              ? 'Either the matcher settled everything deterministically, or the pipeline has not run yet.'
              : `No pending pair scores ${TIER_LABELS[tier]}.`
          }
          compact
        />
      ) : (
        <div className={isPlaceholderData ? 'opacity-60 transition-opacity' : undefined} aria-busy={isPlaceholderData}>
          <Table
            minWidth={900}
            colWidths={[
              'auto',
              'auto',
              ...(sharedScore ? [] : ['5rem']),
              '16rem',
              ...(sharedSource ? [] : ['8rem']),
              '14rem',
            ]}
          >
            <THead>
              <tr>
                <Th>Unmatched row</Th>
                <Th>Proposed record</Th>
                {!sharedScore && <Th align="right">Score</Th>}
                <Th>Why</Th>
                {!sharedSource && <Th>Source</Th>}
                <Th align="right">Decide</Th>
              </tr>
            </THead>
            <tbody>
              {pager.pageItems.map((row) => {
                const why = whyParts(row, countryInHeader);
                const twins = sameName.get(`${row.candidateLabel}\u0000${row.proposedCanonicalId ?? ''}`) ?? 1;
                const fromAttio = row.source === 'attio';
                const fallback = [
                  row.sourceDomain ? row.sourceDomain : 'no company domain',
                  twins > 1 ? `${twins} ${fromAttio ? 'Attio records' : 'rows'} with this name` : null,
                ]
                  .filter(Boolean)
                  .join(' · ');
                return (
                  <Tr key={row.id}>
                    <Td className="min-w-0">
                      <div className="flex min-w-0 items-start gap-1">
                        <div className="min-w-0 flex-1">
                          <CandidateLabel label={tidy(row.candidateLabel)} fallback={fallback} />
                        </div>
                        {row.sourceAttioRecordId && (
                          <AttioCompanyLink recordId={row.sourceAttioRecordId} name={labelNameOf(row.candidateLabel)} />
                        )}
                      </div>
                    </Td>
                    <Td className="min-w-0 text-text-secondary">
                      {row.proposedLabel ? (
                        <div className="flex min-w-0 items-start gap-1">
                          <div className="min-w-0 flex-1">
                            <CandidateLabel label={tidy(row.proposedLabel)} domain={row.proposedDomain ?? null} />
                          </div>
                          {row.proposedAttioRecordId && (
                            <AttioCompanyLink recordId={row.proposedAttioRecordId} name={row.proposedLabel} />
                          )}
                        </div>
                      ) : (
                        <Nil />
                      )}
                    </Td>
                    {!sharedScore && (
                      <Td align="right" className="tabular-nums text-text-primary">
                        {row.score.toFixed(2)}
                      </Td>
                    )}
                    <Td className="text-body-xs text-text-tertiary">
                      {why.length ? why.join(' · ') : <Nil />}
                    </Td>
                    {!sharedSource && <Td className="text-body-xs text-text-tertiary">{humanize(row.source)}</Td>}
                    <Td align="right">
                      <span className="inline-flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={decide.isPending}
                          onClick={() => decide.mutate({ id: row.id, decision: 'rejected' })}
                        >
                          Different
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={decide.isPending}
                          onClick={() => decide.mutate({ id: row.id, decision: 'accepted' })}
                        >
                          Same company
                        </Button>
                      </span>
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
          {(capped || hiddenJunk > 0 || autoLinked > 0) && (
            <TableFooter action={hiddenJunk > 0 ? <InfoTip label="What is left out">{JUNK_NOTE}</InfoTip> : undefined}>
              {[
                capped
                  ? `Top ${formatNumber(rows.length)} of ${formatNumber(inTier)} ${tier === 'all' ? 'pending pairs' : `pairs scored ${TIER_LABELS[tier]}`}, most ambiguous first · decide these and the next ones load`
                  : null,
                autoLinked > 0
                  ? `${formatNumber(autoLinked)} pairs on one domain (subdomain, www or country prefix of the same company domain) linked automatically, not listed`
                  : null,
                hiddenJunk > 0
                  ? `${leftOutText(hiddenJunk)}${hiddenText ? `: ${hiddenText}` : ''}; not in the Pending count`
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </TableFooter>
          )}
          <Pagination
            page={pager.page}
            pageCount={pager.pageCount}
            total={pager.total}
            pageSize={pager.pageSize}
            onPage={pager.setPage}
          />
        </div>
      )}
    </Card>
    </div>
  );
}
